import Dexie, { type Table } from 'dexie'
import { TreelessWarranty, type Session } from 'pages/session/store/Session'
import { type FeedbackProps } from 'pages/session/feedback/Feedback'
import {
  type TreelessDiagnostics,
  type Problem,
  type DiagnosticEvent,
  type DiagnosticSnapshot,
  type ProductInfo,
} from 'tsp-types'

export type SessionDbType = 'TREELESS' | 'GUIDE'

export interface SessionDb {
  id: string
  type: SessionDbType
  data: Session['data']
  treelessData?: TreelessDiagnostics
  step?: Session['step']
  problems?: Record<string, Problem>
  status: Session['status']
  startedOn: Date
  feedback?: FeedbackProps
  meta?: Record<any, any>
  mtime: number
  syncTime?: number
  projectId: string
  user: string
  productInfo?: ProductInfo
  rootGuideId?: string
  treelessWarranty?: TreelessWarranty[]
  telemetryData?: Record<string, string | number>
  /** Session ID as stored in analytics (Postgres). Use this for ticket/event payloads so they match the sessions row. */
  analyticsSessionId?: string
  /** How this session was initiated. Mirrors Mixpanel source values. */
  entry_point?: 'direct' | 'my-machine' | 'external'
  /**
   * Full serialised DiagnosticSnapshot, written by snapshotToSessionDb on every
   * applyAndPersist call. Allows sessionDbToSnapshot to restore the exact snapshot
   * after a page reload without relying on the legacy problemsWithStatus / meta.finished
   * fields (which are no longer written by the event pipeline).
   *
   * Not indexed — no Dexie schema migration required.
   */
  diagnosticSnapshot?: DiagnosticSnapshot
}

export interface PendingEventRow {
  id?: number
  sessionId: string
  event: DiagnosticEvent
  createdAt: number // ms timestamp — used for TTL
  attempts: number // used only to compute backoff delay, not as a cap
  nextRetryAt: number // ms timestamp — row eligible when nextRetryAt <= Date.now()
}

export class IndexedDatabase extends Dexie {
  sessions!: Table<SessionDb>
  pendingEvents!: Table<PendingEventRow>

  constructor() {
    super('tspDb')
    // Dexie maps version(2) -> IndexedDB version 20 and version(3) -> 30.
    // Keep this monotonic so local developer databases created by newer code
    // do not fail when switching branches or stashing changes.
    this.version(3).stores({
      sessions:
        'id, data, step, status, startedOn, mtime, syncTime, type, user',
    })
    // v4: adds pendingEvents for the durable diagnostic event outbox.
    // Migration is additive-only — existing sessions data is preserved.
    this.version(4).stores({
      sessions:
        'id, data, step, status, startedOn, mtime, syncTime, type, user',
      pendingEvents: '++id, sessionId, createdAt',
    })
    // v5: adds nextRetryAt index for TTL + exponential backoff.
    // Upgrade sets nextRetryAt = 0 on existing rows so they are immediately eligible.
    this.version(5)
      .stores({
        sessions:
          'id, data, step, status, startedOn, mtime, syncTime, type, user',
        pendingEvents: '++id, sessionId, createdAt, nextRetryAt',
      })
      .upgrade(tx =>
        tx
          .table('pendingEvents')
          .toCollection()
          .modify(row => {
            row.nextRetryAt = 0
          })
      )
  }

  async updateSessionIfPresent(sessionId: string, changes: Partial<Session>) {
    const dbData = await this.sessions.get(sessionId)
    if (!dbData) return
    void db.sessions.update(sessionId, {
      ...changes,
      mtime: Date.now(),
    } as any)
  }

  async getSessionIfPresent(sessionId: string) {
    const dbData = await this.sessions.get(sessionId)
    if (!dbData) return
    return dbData
  }
}

export const db = new IndexedDatabase()
